import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, Check, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function NeedsReview() {
  const { user } = useAuth();
  const [data, setData] = useState({ counts: { open: 0, relinked: 0, dismissed: 0 }, rows: [] });
  const [tab, setTab] = useState("open");
  const [projSubs, setProjSubs] = useState({}); // project_id -> [subtypes]
  const [dlg, setDlg] = useState({ open: false, item: null, target: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get(`/needs-review/legacy-budget?status=${tab}`);
    setData(r.data);
  };

  useEffect(() => { load(); }, [tab]);

  if (user?.role !== "Admin") {
    return <div className="p-8 text-sm text-slate-500" data-testid="nr-forbidden">Only Super Admin can access the Needs Review queue.</div>;
  }

  const openRelink = async (item) => {
    if (!projSubs[item.project_id]) {
      try {
        const r = await api.get(`/projects/${item.project_id}/subtypes?include_disabled=true`);
        setProjSubs(prev => ({ ...prev, [item.project_id]: r.data || [] }));
      } catch { setProjSubs(prev => ({ ...prev, [item.project_id]: [] })); }
    }
    setDlg({ open: true, item, target: "" });
  };

  const relink = async () => {
    if (!dlg.target) { toast.error("Pick a target sub-type"); return; }
    setBusy(true);
    try {
      await api.post(`/needs-review/legacy-budget/${dlg.item.id}/relink`, { target_subtype_id: dlg.target });
      toast.success("Re-linked to Budget V2");
      setDlg({ open: false, item: null, target: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const dismiss = async (item) => {
    if (!window.confirm(`Dismiss this ${item.source_collection} row? It will stay unlinked.`)) return;
    try {
      await api.post(`/needs-review/legacy-budget/${item.id}/dismiss`);
      toast.success("Dismissed"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="needs-review-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Admin · Migration triage</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Needs Review — Legacy Budget Links</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Reimbursements, advances and legacy expenses that point at a retired Budget V1 line and could not be
          auto-matched to a Budget V2 sub-type. Pick the right V2 sub-type below, or dismiss.
        </p>
      </div>

      <div className="flex gap-3">
        <StatPill label="Open" value={data.counts.open} tone="amber" />
        <StatPill label="Re-linked" value={data.counts.relinked} tone="emerald" />
        <StatPill label="Dismissed" value={data.counts.dismissed} tone="slate" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="open" data-testid="nr-tab-open">Open ({data.counts.open})</TabsTrigger>
          <TabsTrigger value="relinked" data-testid="nr-tab-relinked">Re-linked ({data.counts.relinked})</TabsTrigger>
          <TabsTrigger value="dismissed" data-testid="nr-tab-dismissed">Dismissed ({data.counts.dismissed})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <Card>
            <CardContent className="p-0">
              {data.rows.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500" data-testid="nr-empty">No items in <b>{tab}</b>.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-3 py-2">Source</th>
                      <th className="text-left px-3 py-2">Project</th>
                      <th className="text-left px-3 py-2">Legacy line</th>
                      <th className="text-right px-3 py-2">Amount</th>
                      <th className="text-left px-3 py-2">Reason</th>
                      <th className="text-right px-3 py-2 pr-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map(row => (
                      <tr key={row.id} className="border-b hover:bg-slate-50" data-testid={`nr-row-${row.id}`}>
                        <td className="px-3 py-2"><Badge variant="outline">{row.source_collection}</Badge> <span className="text-slate-500 text-xs">{row.source_id.slice(0,8)}…</span></td>
                        <td className="px-3 py-2">{row.project_code || row.project_name || row.project_id?.slice(0,8)}</td>
                        <td className="px-3 py-2"><span className="text-slate-500 text-xs">{row.legacy_head_name || "—"}</span> / <span className="text-xs">{row.legacy_subtype_name || "(no name)"}</span></td>
                        <td className="px-3 py-2 text-right font-mono">₹ {Number(row.amount || 0).toLocaleString("en-IN")}</td>
                        <td className="px-3 py-2 text-xs text-amber-800">{row.reason}</td>
                        <td className="px-3 py-2 text-right pr-4">
                          {tab === "open" ? (
                            <div className="flex gap-2 justify-end">
                              <Button size="sm" onClick={() => openRelink(row)} data-testid={`nr-relink-${row.id}`}>
                                Re-link <ArrowRight className="w-3 h-3 ml-1" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => dismiss(row)} data-testid={`nr-dismiss-${row.id}`}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">
                              {row.resolved_by_name} · {row.resolution?.target_subtype_name || "—"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dlg.open} onOpenChange={o => setDlg(d => ({ ...d, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Re-link to a Budget V2 sub-type</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <div><b>Source:</b> {dlg.item?.source_collection} · {dlg.item?.source_id}</div>
            <div><b>Legacy head/sub-type:</b> {dlg.item?.legacy_head_name || "—"} / {dlg.item?.legacy_subtype_name || "(no name)"}</div>
            <div><b>Amount:</b> ₹ {Number(dlg.item?.amount || 0).toLocaleString("en-IN")}</div>
            <div className="pt-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Target Budget V2 sub-type</div>
              <Select value={dlg.target} onValueChange={v => setDlg(d => ({ ...d, target: v }))}>
                <SelectTrigger data-testid="nr-target-select">
                  <SelectValue placeholder={(projSubs[dlg.item?.project_id] || []).length ? "Pick a sub-type…" : "No V2 sub-types exist for this project"} />
                </SelectTrigger>
                <SelectContent>
                  {(projSubs[dlg.item?.project_id] || []).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.code} · {s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg({ open: false, item: null, target: "" })}>Cancel</Button>
            <Button onClick={relink} disabled={busy || !dlg.target} data-testid="nr-relink-confirm">
              <Check className="w-4 h-4 mr-1" /> Re-link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatPill = ({ label, value, tone }) => {
  const bg = { amber: "bg-amber-50 border-amber-200 text-amber-900", emerald: "bg-emerald-50 border-emerald-200 text-emerald-900", slate: "bg-slate-50 border-slate-200 text-slate-700" }[tone];
  return (
    <div className={`px-3 py-1.5 rounded-md border text-sm ${bg}`} data-testid={`nr-stat-${label.toLowerCase()}`}>
      <span className="text-xs font-semibold uppercase tracking-widest opacity-70">{label}</span>
      <span className="ml-2 font-mono font-bold">{value}</span>
    </div>
  );
};
